import { isDrawerPanelView } from '@/shell/drawerPanel';
import { Suspense, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { AlertCircle, CheckCircle2, Mail } from 'lucide-react';
import { useStores } from '@/hooks/useStores';
import { Button } from '@/components/Button';
import { COARSE_HIT } from '@/components/touchTarget';
import { BOTTOM_BAR_ROW, ViewportBottomBar } from '@/components/AppViewport';
import { BottomScrollFade, useBottomScrollFade } from '@/components/BottomScrollFade';
import { lazyRoute } from '@/routing/lazyRoute';
import { useT } from '@/i18n/LocaleProvider';
import { registerEnglishArea } from '@/i18n/catalogue';
import { banners } from '@featit/shared/i18n/messages/en/banners';

/*
 * The place a refusal named (T-0824), drawn only under a refusal that names one: its code (the
 * destination table and the permissions read) loads then, and the launch entry carries none of it.
 */
const RefusalPlaceLink = lazyRoute(
  () => import('@/features/settings/refusalDestinations'),
  (module) => module.RefusalPlaceLink,
);

registerEnglishArea('banners', banners);

/**
 * "Confirm your email address" — F-0160 §3.1, AC-18's resend control.
 *
 * ⚠️ **Near-dead since D-0041 = a** (F-0160 AC-25): an account whose address is unconfirmed can no
 * longer sign in, and every account that existed when that rule arrived was grandfathered as
 * verified, so a signed-in account is always verified and this renders nothing. The person who needs
 * a new link now meets it on the sign-in screen (`UnverifiedSignInNotice`), which has no session to
 * lean on.
 *
 * Kept rather than deleted, deliberately: it still renders nothing for a verified account (its test
 * says so), and it is the one place a session held by an unverified account — an older client, a
 * credential minted before the rule, a future door that forgets the check — would still be told what
 * to do, instead of failing silently.
 *
 * Rendered once, above the router's routes (`App.tsx`'s `RouterRoot`), so it appears on every screen
 * a signed-in person can reach and renders nothing at all — `authStore.user` is null — on the public
 * ones (`/login`, `/register`, `/forgot-password`, `/reset-password`, `/verify-email`).
 */
export const EmailVerificationBanner = observer(function EmailVerificationBanner() {
  const t = useT();
  const { authStore } = useStores();
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const scroller = useBottomScrollFade<HTMLDivElement>();

  if (!authStore.user || authStore.user.verifiedAt || dismissed || isDrawerPanelView()) return null;

  async function handleResend() {
    setSent(false);
    const ok = await authStore.resendEmailVerification();
    if (ok) setSent(true);
  }

  return (
    <ViewportBottomBar order="account">
      <div
        ref={scroller.ref}
        role="status"
        data-bottom-bar="email-verification"
        /*
        I-0855: at the largest text size the banner's words filled a phone's whole screen and left the
        page no room, and the address ran past the side. It holds at most 40% of the window and
        scrolls inside that, so the page keeps the rest; and a word or address wider than the line
        breaks rather than being cut. At an ordinary size it is one short line, well under the cap.
        I-1450: while it scrolls it can take focus, so the keyboard scrolls it too, and a fade over
        its bottom edge says more follows until it is scrolled to the end.
      */
        tabIndex={scroller.overflowing ? 0 : undefined}
        onFocus={scroller.onFocus}
        onKeyDown={scroller.onKeyDown}
        className={`flex ${BOTTOM_BAR_ROW} max-h-[40dvh] flex-wrap items-center gap-2.5 overflow-y-auto border-t border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-950 [overflow-wrap:anywhere] dark:border-amber-700/60 dark:bg-[#2b2618] dark:text-amber-100 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-amber-700 dark:focus-visible:outline-amber-300`}
      >
        {/*
        I-1307 (CFC-f5): the icon and the words are one group with a floor, 14 of the banner's own
        em or the whole line when that is less. At the largest text size Resend alone was 166 px of
        a phone's 358, the words kept 122 and broke "Confir / m", "addre / ss"; with the floor the
        two buttons wrap under the words. At the default size the group already measured more
        (206 px at 390), so nothing moves.
      */}
        <span className="flex min-w-[min(100%,14em)] flex-1 items-center gap-2.5">
          <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="font-medium">{t('banners.verify.confirm')}</span>{' '}
            {sent ? (
              <span className="inline-flex items-center gap-1 text-amber-800 dark:text-amber-200">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                {t('banners.verify.sent')}
              </span>
            ) : authStore.error ? (
              <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-300">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                {authStore.error}
                {/* T-0824: mail not set up is fixed in Global settings → Email, for whoever may. */}
                {authStore.errorDestination ? (
                  <Suspense fallback={null}>
                    <RefusalPlaceLink
                      id={authStore.errorDestination}
                      testId="verification-banner-destination"
                    />
                  </Suspense>
                ) : null}
              </span>
            ) : (
              <span className="text-amber-800 dark:text-amber-200">
                {t('banners.verify.weSent', { email: authStore.user.email })}
              </span>
            )}
          </span>
        </span>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className={COARSE_HIT}
          loading={authStore.loading}
          onClick={() => void handleResend()}
        >
          {t('banners.verify.resend')}
        </Button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className={`${COARSE_HIT} rounded px-2 py-1 text-amber-700 evx-link-hover dark:text-amber-300`}
        >
          {t('banners.dismiss')}
        </button>
        <BottomScrollFade
          overflowing={scroller.overflowing}
          moreBelow={scroller.moreBelow}
          fromClassName="from-amber-50 dark:from-[#2b2618]"
        />
      </div>
    </ViewportBottomBar>
  );
});
